// DeviceConfigureForm – modal for offline ops: event/lifecycle/note tools.

import React, { JSX, useEffect, useRef, useState } from 'react';
import useNonTerminalDeviceEventAssociations from '../../hooks/useNonTerminalDeviceEventAssociations';
import InlineSpinner from '../shared/InlineSpinner';
import { addDeviceNotesRequest } from '../shared/LeaveDeviceNoteForm';
import type { DeviceNoteType } from '../shared/types/DeviceNoteTypes';
import SubmitLifecycleReportOperation, {
    LIFECYCLE_REPORT_MESSAGE_MAX,
    type LifecycleFlagsState,
} from './panels/SubmitLifecycleReportOperation';
import OperationSelector from './panels/OperationSelector';
import type { DeviceConfigureOperation } from './types/DeviceConfigureTypes';
import LeaveDeviceNoteOperation from './panels/LeaveDeviceNoteOperation';
import RunDeviceValidationOperation from './panels/RunDeviceValidationOperation';
import RemoveDeviceMappingOperation from './panels/RemoveDeviceMappingOperation';
import { PortaledLayer } from '../shared/PortaledLayer';
import { formatLifecycleSummary, normalizeLifecycleSnapshot } from '../../helpers/deviceLifecycle';
import { resolveDeviceMappingId, type DeviceMappingRow } from '../../helpers/deviceMappingRows';
import { pairedRearIdFromFront } from '../../helpers/devicePairing';

type DeviceConfigureFormProps = {
    selectedIds: string[];
    onClose: () => void;
    initialOperation?: DeviceConfigureOperation;
    /** Tailwind z-index class for the modal overlay (e.g. when opened above device dossier). */
    overlayZIndexClass?: string;
    /** Pre-select lifecycle event/race (e.g. from device dossier assignment). */
    initialLifecycleEventId?: string;
    initialLifecycleRaceId?: string;
    /** Pre-select note event (e.g. from device dossier assignment). */
    initialNoteEventId?: string;
    /** When lifecycle rows change in DynamoDB, parent can refetch device lifecycle (e.g. Devices table). */
    onLifecycleDirty?: () => void;
    /** When device mapping rows are removed, parent can refetch the device list. */
    onDeviceMappingsDirty?: () => void;
    onRunDeviceValidation?: (params: {
        eventId: string;
        raceId: string;
        deviceIds: string[];
    }) => Promise<void> | void;
};

// ---- Helpers ----

function dispatchToast(
    type: 'toast-info' | 'toast-error',
    message: string | { message: string;[k: string]: unknown },
): void {
    window.dispatchEvent(new CustomEvent(type, { detail: message }));
}

// ---- Component ----

export default function DeviceConfigureForm(
    props: DeviceConfigureFormProps,
): JSX.Element {
    const {
        selectedIds,
        onClose,
        initialOperation,
        overlayZIndexClass = 'z-50',
        initialLifecycleEventId = '',
        initialLifecycleRaceId = '',
        initialNoteEventId = '',
        onLifecycleDirty,
        onDeviceMappingsDirty,
        onRunDeviceValidation,
    } = props;

    const [operation, setOperation] =
        useState<DeviceConfigureOperation>(initialOperation ?? 'LEAVE_DEVICE_NOTE');

    const {
        events,
        eventsLoading,
        deviceEventMap,
        reload,
    } = useNonTerminalDeviceEventAssociations(selectedIds);

    const [submitting, setSubmitting] = useState(false);

    // Leave note (S3 append-only notes).
    const [noteType, setNoteType] = useState<DeviceNoteType>('info');
    const [noteEventId, setNoteEventId] = useState<string>('');
    const [noteBody, setNoteBody] = useState<string>('');
    // Tracks the selectedIds set the event/eventId auto-fill effects have already run
    // for, so clearing the dropdown back to "No event" sticks instead of the effect
    // re-firing (it depends on the very field the user just cleared) and stomping the
    // manual choice back to the device's associated event on every render.
    const noteEventAutoFillKeyRef = useRef('');
    const lifecycleEventAutoFillKeyRef = useRef('');

    const [lifecycleFlags, setLifecycleFlags] = useState<LifecycleFlagsState>({
        lost: false,
        decommissioned: false,
        damaged: false,
        errored: false,
    });
    const [lifecycleReportMessage, setLifecycleReportMessage] = useState('');
    const [selectedLifecycleEventId, setSelectedLifecycleEventId] = useState('');
    const [selectedLifecycleRaceId, setSelectedLifecycleRaceId] = useState('');
    const [lifecycleRaces, setLifecycleRaces] = useState<{ raceId: string; name?: string }[]>([]);
    const [lifecycleRacesLoading, setLifecycleRacesLoading] = useState(false);
    const [lifecyclePrefillLoading, setLifecyclePrefillLoading] = useState(false);
    const [lifecyclePrefillSummary, setLifecyclePrefillSummary] = useState('');
    const [lifecyclePrefillMixedSelection, setLifecyclePrefillMixedSelection] = useState(false);
    const [attrRaceCount, setAttrRaceCount] = useState('');
    const [attrWaterproofedBy, setAttrWaterproofedBy] = useState('');
    const [selectedValidationEventId, setSelectedValidationEventId] = useState('');
    const [selectedValidationRaceId, setSelectedValidationRaceId] = useState('');
    const [validationRaces, setValidationRaces] = useState<{ raceId: string; name?: string }[]>([]);
    const [validationRacesLoading, setValidationRacesLoading] = useState(false);
    const [removeMappingConfirmed, setRemoveMappingConfirmed] = useState(false);

    useEffect(() => {
        if (
            operation === 'LEAVE_DEVICE_NOTE'
            || operation === 'RUN_DEVICE_VALIDATION'
            || operation === 'SUBMIT_LIFECYCLE_REPORT'
        ) {
            void reload();
        }
    }, [operation, reload]);

    useEffect(() => {
        if (operation !== 'SUBMIT_LIFECYCLE_REPORT') return;
        const eid = String(initialLifecycleEventId ?? '').trim();
        const rid = String(initialLifecycleRaceId ?? '').trim();
        if (eid) setSelectedLifecycleEventId(eid);
        if (rid) setSelectedLifecycleRaceId(rid);
    }, [operation, initialLifecycleEventId, initialLifecycleRaceId]);

    useEffect(() => {
        if (operation !== 'LEAVE_DEVICE_NOTE') return;
        const eid = String(initialNoteEventId ?? '').trim();
        if (eid) setNoteEventId(eid);
    }, [operation, initialNoteEventId]);

    useEffect(() => {
        if (operation !== 'SUBMIT_LIFECYCLE_REPORT') return;
        const key = selectedIds.join('');
        if (lifecycleEventAutoFillKeyRef.current === key) return;
        const firstId = String(selectedIds[0] ?? '').trim();
        if (!firstId) return;
        const assoc = deviceEventMap[firstId];
        const eid = String(assoc?.eventId ?? '').trim();
        if (!eid) return;
        lifecycleEventAutoFillKeyRef.current = key;
        setSelectedLifecycleEventId(eid);
        const rid = String(assoc?.raceId ?? '').trim();
        if (rid) setSelectedLifecycleRaceId(rid);
    }, [operation, selectedIds, deviceEventMap]);

    useEffect(() => {
        if (operation !== 'LEAVE_DEVICE_NOTE') return;
        const key = selectedIds.join('');
        if (noteEventAutoFillKeyRef.current === key) return;
        const firstId = String(selectedIds[0] ?? '').trim();
        if (!firstId) return;
        const assoc = deviceEventMap[firstId];
        const eid = String(assoc?.eventId ?? '').trim();
        if (!eid) return;
        noteEventAutoFillKeyRef.current = key;
        setNoteEventId(eid);
    }, [operation, selectedIds, deviceEventMap]);

    useEffect(() => {
        if (operation !== 'RUN_DEVICE_VALIDATION' || !selectedValidationEventId) {
            setValidationRaces([]);
            setSelectedValidationRaceId('');
            return;
        }
        const api = window.eventsAPI?.listEventRaces;
        if (!api) {
            setValidationRaces([]);
            return;
        }
        let cancelled = false;
        setValidationRacesLoading(true);
        void api(selectedValidationEventId)
            .then((res: unknown) => {
                if (cancelled) return;
                const r = res as { ok?: boolean; data?: { races?: { raceId?: string; name?: string }[] } };
                if (!r?.ok) {
                    setValidationRaces([]);
                    setSelectedValidationRaceId('');
                    return;
                }
                const rows = Array.isArray(r.data?.races) ? r.data!.races! : [];
                const mapped = rows
                    .map((x) => ({
                        raceId: String(x?.raceId ?? '').trim(),
                        name: x?.name != null ? String(x.name) : undefined,
                    }))
                    .filter((x) => x.raceId);
                setValidationRaces(mapped);
                setSelectedValidationRaceId((prev) =>
                    prev && mapped.some((m) => m.raceId === prev) ? prev : mapped[0]?.raceId ?? '',
                );
            })
            .catch(() => {
                if (!cancelled) {
                    setValidationRaces([]);
                    setSelectedValidationRaceId('');
                }
            })
            .finally(() => {
                if (!cancelled) setValidationRacesLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [operation, selectedValidationEventId]);

    useEffect(() => {
        if (operation !== 'SUBMIT_LIFECYCLE_REPORT' || !selectedLifecycleEventId) {
            setLifecycleRaces([]);
            setSelectedLifecycleRaceId('');
            setLifecycleRacesLoading(false);
            return;
        }
        const api = window.eventsAPI?.listEventRaces;
        if (!api) {
            setLifecycleRaces([]);
            return;
        }
        let cancelled = false;
        setLifecycleRacesLoading(true);
        void api(selectedLifecycleEventId)
            .then((res: unknown) => {
                if (cancelled) return;
                const r = res as { ok?: boolean; data?: { races?: { raceId?: string; name?: string }[] } };
                if (!r?.ok) {
                    setLifecycleRaces([]);
                    setSelectedLifecycleRaceId('');
                    return;
                }
                const rows = Array.isArray(r.data?.races) ? r.data!.races! : [];
                const mapped = rows
                    .map((x) => ({
                        raceId: String(x?.raceId ?? '').trim(),
                        name: x?.name != null ? String(x.name) : undefined,
                    }))
                    .filter((x) => x.raceId);
                setLifecycleRaces(mapped);
                setSelectedLifecycleRaceId((prev) =>
                    prev && mapped.some((m) => m.raceId === prev) ? prev : '',
                );
            })
            .catch(() => {
                if (!cancelled) {
                    setLifecycleRaces([]);
                    setSelectedLifecycleRaceId('');
                }
            })
            .finally(() => {
                if (!cancelled) setLifecycleRacesLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [operation, selectedLifecycleEventId]);

    useEffect(() => {
        if (operation !== 'SUBMIT_LIFECYCLE_REPORT') {
            setLifecyclePrefillLoading(false);
            setLifecyclePrefillSummary('');
            setLifecyclePrefillMixedSelection(false);
            return;
        }
        const deviceIds = selectedIds.map((id) => String(id || '').trim()).filter(Boolean);
        if (deviceIds.length === 0) {
            setLifecycleFlags({ lost: false, decommissioned: false, damaged: false, errored: false });
            setLifecycleReportMessage('');
            setLifecyclePrefillLoading(false);
            setLifecyclePrefillSummary('');
            setLifecyclePrefillMixedSelection(false);
            return;
        }
        const api = window.eventsAPI?.getDeviceLifecycle;
        if (!api) {
            setLifecyclePrefillLoading(false);
            setLifecyclePrefillSummary('');
            setLifecyclePrefillMixedSelection(false);
            return;
        }

        let cancelled = false;
        setLifecyclePrefillLoading(true);
        void api({ deviceIds })
            .then((res: unknown) => {
                if (cancelled) return;
                const r = res as { ok?: boolean; data?: { devices?: unknown[] } } | null | undefined;
                if (!r?.ok) {
                    setLifecyclePrefillSummary('');
                    setLifecyclePrefillMixedSelection(false);
                    return;
                }
                const rows = Array.isArray(r.data?.devices) ? r.data.devices : [];
                const byId: Record<string, unknown> = {};
                for (const raw of rows) {
                    if (!raw || typeof raw !== 'object') continue;
                    const row = raw as Record<string, unknown>;
                    const did = String(row.deviceId ?? '').trim();
                    if (did) byId[did] = row;
                }

                const firstId = deviceIds[0];
                const firstSnap = normalizeLifecycleSnapshot(byId[firstId]);
                const firstRow = byId[firstId] as Record<string, unknown> | undefined;
                const flags: LifecycleFlagsState = {
                    lost: !!firstSnap.lost,
                    decommissioned: !!firstSnap.decommissioned,
                    damaged: !!firstSnap.damaged,
                    errored: !!firstSnap.errored,
                };
                setLifecycleFlags(flags);
                setLifecycleReportMessage(String(firstSnap.reason ?? ''));
                setLifecyclePrefillSummary(`Loaded from ${firstId}`);
                const lifecycleEventId = String(firstRow?.eventId ?? '').trim();
                const lifecycleRaceId = String(firstRow?.raceId ?? '').trim();
                if (lifecycleEventId) setSelectedLifecycleEventId(lifecycleEventId);
                if (lifecycleRaceId) setSelectedLifecycleRaceId(lifecycleRaceId);

                const mixed = deviceIds.slice(1).some((id) => {
                    const s = normalizeLifecycleSnapshot(byId[id]);
                    return (
                        !!s.lost !== flags.lost
                        || !!s.decommissioned !== flags.decommissioned
                        || !!s.damaged !== flags.damaged
                        || !!s.errored !== flags.errored
                        || String(s.reason ?? '') !== String(firstSnap.reason ?? '')
                    );
                });
                setLifecyclePrefillMixedSelection(mixed);
            })
            .catch(() => {
                if (!cancelled) {
                    setLifecyclePrefillSummary('');
                    setLifecyclePrefillMixedSelection(false);
                }
            })
            .finally(() => {
                if (!cancelled) setLifecyclePrefillLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [operation, selectedIds]);

    useEffect(() => {
        if (operation !== 'SUBMIT_LIFECYCLE_REPORT') return;
        const deviceIds = selectedIds.map((id) => String(id || '').trim()).filter(Boolean);
        if (deviceIds.length === 0) {
            setAttrRaceCount('');
            setAttrWaterproofedBy('');
            return;
        }
        const api = window.eventsAPI?.getDeviceAttributes;
        if (!api) return;

        let cancelled = false;
        void api({ deviceId: deviceIds[0] })
            .then((res: unknown) => {
                if (cancelled) return;
                const r = res as
                    | { ok?: boolean; data?: { device?: { raceCount?: number; waterproofedBy?: string } | null; devices?: Array<{ raceCount?: number; waterproofedBy?: string }> } }
                    | null
                    | undefined;
                if (!r?.ok) return;
                const row = r.data?.device || (Array.isArray(r.data?.devices) ? r.data?.devices[0] : null);
                setAttrRaceCount(
                    typeof row?.raceCount === 'number' && Number.isFinite(row.raceCount) ? String(row.raceCount) : '',
                );
                setAttrWaterproofedBy(String(row?.waterproofedBy ?? '').trim());
            })
            .catch(() => {});

        return () => {
            cancelled = true;
        };
    }, [operation, selectedIds]);

    /** Switch operation and clear operation-specific state. */
    function handleOperationChange(value: DeviceConfigureOperation): void {
        setOperation(value);
        setNoteEventId('');
        setNoteBody('');
        setNoteType('info');
        noteEventAutoFillKeyRef.current = '';
        setLifecycleFlags({ lost: false, decommissioned: false, damaged: false, errored: false });
        setLifecycleReportMessage('');
        setSelectedLifecycleEventId('');
        setSelectedLifecycleRaceId('');
        setLifecycleRaces([]);
        lifecycleEventAutoFillKeyRef.current = '';
        setSelectedValidationEventId('');
        setSelectedValidationRaceId('');
        setValidationRaces([]);
        setRemoveMappingConfirmed(false);
    }

    /** Leave an append-only note for each selected device. */
    async function submitLeaveDeviceNotes(): Promise<void> {
        const result = await addDeviceNotesRequest({
            deviceIds: selectedIds,
            type: noteType,
            message: noteBody,
            eventId: noteEventId,
        });
        if (!result.ok) {
            dispatchToast('toast-error', result.errorMessage);
            return;
        }
        dispatchToast(
            'toast-info',
            `Saved note for ${selectedIds.length} device${selectedIds.length === 1 ? '' : 's'}`,
        );
        onClose();
    }

    /** Admin: set DynamoDB lifecycle + optional reason for each selected device. */
    async function submitLifecycleReport(): Promise<void> {
        if (!window.eventsAPI?.setDeviceLifecycle) {
            dispatchToast('toast-error', 'Lifecycle API is not available');
            return;
        }
        const msg = lifecycleReportMessage.trim();
        if (!msg) {
            dispatchToast('toast-error', 'Message is required');
            return;
        }
        if (msg.length > LIFECYCLE_REPORT_MESSAGE_MAX) {
            dispatchToast(
                'toast-error',
                `Message must be at most ${LIFECYCLE_REPORT_MESSAGE_MAX} characters`,
            );
            return;
        }
        const rcTrimmed = attrRaceCount.trim();
        const wpTrimmed = attrWaterproofedBy.trim();
        const wantAttributes =
            !!window.eventsAPI?.setDeviceAttributes && (rcTrimmed !== '' || wpTrimmed !== '');
        let raceCountVal: number | undefined;
        if (wantAttributes && rcTrimmed !== '') {
            const parsed = Number(rcTrimmed);
            if (!Number.isFinite(parsed) || !Number.isInteger(parsed) || parsed < 0) {
                dispatchToast('toast-error', 'Race count must be a non-negative whole number');
                return;
            }
            raceCountVal = parsed;
        }

        for (const deviceId of selectedIds) {
            const res = await window.eventsAPI.setDeviceLifecycle({
                deviceId,
                eventId: selectedLifecycleEventId || undefined,
                raceId: selectedLifecycleRaceId || undefined,
                lost: lifecycleFlags.lost,
                decommissioned: lifecycleFlags.decommissioned,
                damaged: lifecycleFlags.damaged,
                errored: lifecycleFlags.errored,
                reason: msg,
            });
            if (!res.ok) {
                dispatchToast(
                    'toast-error',
                    String(res.error ?? 'Failed to submit lifecycle report'),
                );
                return;
            }

            if (wantAttributes) {
                const attrPayload: { deviceId: string; raceCount?: number; waterproofedBy?: string } = { deviceId };
                if (raceCountVal !== undefined) attrPayload.raceCount = raceCountVal;
                if (wpTrimmed !== '') attrPayload.waterproofedBy = wpTrimmed;
                const ares = await window.eventsAPI.setDeviceAttributes(attrPayload);
                if (!ares.ok) {
                    dispatchToast('toast-error', String(ares.error ?? 'Failed to update device attributes'));
                    return;
                }
            }
        }
        dispatchToast(
            'toast-info',
            `Lifecycle updated for ${selectedIds.length} device${selectedIds.length === 1 ? '' : 's'}`,
        );
        onLifecycleDirty?.();
        onClose();
    }

    async function submitRunDeviceValidation(): Promise<void> {
        if (!selectedValidationEventId) {
            dispatchToast('toast-error', 'Select an event first');
            return;
        }
        if (!selectedValidationRaceId) {
            dispatchToast('toast-error', 'Select a race first');
            return;
        }
        if (!onRunDeviceValidation) {
            dispatchToast('toast-error', 'Device validation is not available');
            return;
        }
        await onRunDeviceValidation({
            eventId: selectedValidationEventId,
            raceId: selectedValidationRaceId,
            deviceIds: selectedIds,
        });
        onClose();
    }

    /** Admin: delete each selected device's row from the device mappings table. */
    async function submitRemoveDeviceMapping(): Promise<void> {
        if (!removeMappingConfirmed) {
            dispatchToast('toast-error', 'Confirm the removal first');
            return;
        }
        if (!window.eventsAPI?.getDeviceMappings || !window.eventsAPI?.putDeviceMappings) {
            dispatchToast('toast-error', 'Device mappings API is not available');
            return;
        }

        const ids = selectedIds.map((id) => String(id ?? '').trim()).filter((id) => id.length > 0);
        try {
            const res = await window.eventsAPI.getDeviceMappings();
            if (!res?.ok) throw new Error(res?.error || 'Failed to load device mappings');
            const raw = (Array.isArray(res?.data?.devices) ? res.data.devices : []) as DeviceMappingRow[];

            // The mappings table only stores rear rows; a selected front ID's row lives
            // under its paired rear ID (see normalizeDeviceMappingRows' front synthesis).
            const removeSet = new Set(ids.map((id) => (pairedRearIdFromFront(id) ?? id).toLowerCase()));
            const remaining = raw.filter((row) => !removeSet.has(resolveDeviceMappingId(row).toLowerCase()));

            const devices = remaining.map((row) => {
                const out: Record<string, string> = {};
                Object.entries(row).forEach(([k, v]) => {
                    out[k] = v == null ? '' : String(v);
                });
                return out;
            });
            const putRes = await window.eventsAPI.putDeviceMappings(devices);
            if (!putRes?.ok) throw new Error(putRes?.error || 'Failed to save device mappings');

            dispatchToast(
                'toast-info',
                `Removed ${ids.length} device${ids.length === 1 ? '' : 's'} from device mappings`,
            );
            onDeviceMappingsDirty?.();
            onClose();
        } catch (err: any) {
            dispatchToast('toast-error', err?.message || 'Failed to remove device mappings');
        }
    }

    /** Route to note/lifecycle/validation operations. */
    async function handleSubmit(): Promise<void> {
        if (submitting) return;

        if (selectedIds.length === 0) {
            dispatchToast('toast-error', 'No devices selected');
            return;
        }

        setSubmitting(true);
        try {
            if (operation === 'LEAVE_DEVICE_NOTE') {
                await submitLeaveDeviceNotes();
                return;
            }

            if (operation === 'SUBMIT_LIFECYCLE_REPORT') {
                await submitLifecycleReport();
                return;
            }
            if (operation === 'RUN_DEVICE_VALIDATION') {
                await submitRunDeviceValidation();
                return;
            }
            if (operation === 'REMOVE_DEVICE_MAPPING') {
                await submitRemoveDeviceMapping();
                return;
            }

            dispatchToast('toast-error', 'Unsupported operation');
        } finally {
            setSubmitting(false);
        }
    }

    const isLeaveNote = operation === 'LEAVE_DEVICE_NOTE';
    const isLifecycleReport = operation === 'SUBMIT_LIFECYCLE_REPORT';
    const isRunDeviceValidation = operation === 'RUN_DEVICE_VALIDATION';
    const isRemoveDeviceMapping = operation === 'REMOVE_DEVICE_MAPPING';
    const canRunDeviceValidation = typeof onRunDeviceValidation === 'function';

    useEffect(() => {
        if (canRunDeviceValidation) return;
        if (operation === 'RUN_DEVICE_VALIDATION') {
            setOperation('LEAVE_DEVICE_NOTE');
        }
    }, [canRunDeviceValidation, operation]);

    return (
        <PortaledLayer>
            <div className={`fixed inset-0 ${overlayZIndexClass} flex items-center justify-center bg-black/40 p-4 dark:bg-black/70`}>
                <div className="flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-xl border border-gray-200 bg-white text-gray-800 shadow-xl dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100">
                {/* Modal header: title, selected count, close */}
                <div className="flex items-center justify-between border-b border-gray-200 bg-gray-50 px-4 py-3 dark:border-slate-700 dark:bg-slate-800">
                    <div>
                        <h2 className="text-sm font-semibold text-gray-900 dark:text-slate-100">
                            Offline operations
                        </h2>
                        <p className="text-xs text-gray-500 dark:text-slate-400">
                            {selectedIds.length} device
                            {selectedIds.length === 1 ? '' : 's'} selected
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="text-xs text-gray-500 hover:text-gray-700 dark:text-slate-300"
                    >
                        Close
                    </button>
                </div>

                {/* Operation picker + conditional operation form */}
                <div className="space-y-4 overflow-y-auto px-4 py-3 text-xs">
                    <OperationSelector
                        value={operation}
                        onChange={handleOperationChange}
                        canRunDeviceValidation={canRunDeviceValidation}
                    />

                    {isLeaveNote ? (
                        <LeaveDeviceNoteOperation
                            selectedIds={selectedIds}
                            noteType={noteType}
                            noteEventId={noteEventId}
                            noteBody={noteBody}
                            submitting={submitting}
                            events={events}
                            eventsLoading={eventsLoading}
                            onNoteTypeChange={setNoteType}
                            onNoteEventIdChange={setNoteEventId}
                            onNoteBodyChange={setNoteBody}
                        />
                    ) : null}

                    {isLifecycleReport ? (
                        <SubmitLifecycleReportOperation
                            selectedIds={selectedIds}
                            selectedLifecycleEventId={selectedLifecycleEventId}
                            selectedLifecycleRaceId={selectedLifecycleRaceId}
                            events={events}
                            races={lifecycleRaces}
                            eventsLoading={eventsLoading}
                            racesLoading={lifecycleRacesLoading}
                            flags={lifecycleFlags}
                            lifecycleReportMessage={lifecycleReportMessage}
                            submitting={submitting}
                            prefillLoading={lifecyclePrefillLoading}
                            prefillSummary={lifecyclePrefillSummary}
                            mixedSelection={lifecyclePrefillMixedSelection}
                            onSelectedLifecycleEventIdChange={(id) => {
                                setSelectedLifecycleEventId(id);
                                setSelectedLifecycleRaceId('');
                            }}
                            onSelectedLifecycleRaceIdChange={setSelectedLifecycleRaceId}
                            onFlagsChange={setLifecycleFlags}
                            onLifecycleReportMessageChange={setLifecycleReportMessage}
                            canEditAttributes={!!window.eventsAPI?.setDeviceAttributes}
                            attrRaceCount={attrRaceCount}
                            attrWaterproofedBy={attrWaterproofedBy}
                            onAttrRaceCountChange={setAttrRaceCount}
                            onAttrWaterproofedByChange={setAttrWaterproofedBy}
                        />
                    ) : null}
                    {isRunDeviceValidation ? (
                        <RunDeviceValidationOperation
                            selectedIds={selectedIds}
                            selectedValidationEventId={selectedValidationEventId}
                            selectedValidationRaceId={selectedValidationRaceId}
                            events={events}
                            races={validationRaces}
                            eventsLoading={eventsLoading}
                            racesLoading={validationRacesLoading}
                            submitting={submitting}
                            onSelectedValidationEventIdChange={(id) => {
                                setSelectedValidationEventId(id);
                                setSelectedValidationRaceId('');
                            }}
                            onSelectedValidationRaceIdChange={setSelectedValidationRaceId}
                        />
                    ) : null}
                    {isRemoveDeviceMapping ? (
                        <RemoveDeviceMappingOperation
                            selectedIds={selectedIds}
                            confirmed={removeMappingConfirmed}
                            submitting={submitting}
                            onConfirmedChange={setRemoveMappingConfirmed}
                        />
                    ) : null}
                </div>

                <div className="flex items-center justify-between border-t border-gray-200 bg-gray-50 px-4 py-3 text-[11px] dark:border-slate-700 dark:bg-slate-800">
                    <div className="text-gray-500 dark:text-slate-400">
                        {isLifecycleReport
                            ? `Lifecycle: ${formatLifecycleSummary(lifecycleFlags)}${selectedLifecycleEventId ? ` · ${selectedLifecycleEventId}${selectedLifecycleRaceId ? ` / ${selectedLifecycleRaceId}` : ''}` : ''}`
                            : isRunDeviceValidation
                                ? (selectedValidationEventId && selectedValidationRaceId
                                    ? `Validate: ${selectedValidationEventId} / ${selectedValidationRaceId}`
                                    : 'Select event and race')
                                : isRemoveDeviceMapping
                                    ? `Remove ${selectedIds.length} device${selectedIds.length === 1 ? '' : 's'} from mappings`
                                    : `Note type: ${noteType}`}
                    </div>
                    <div className="flex gap-2">
                        <button
                            type="button"
                            onClick={onClose}
                            className="inline-flex items-center rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-1.5 text-[11px] font-medium text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-800"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={() => void handleSubmit()}
                            disabled={
                                submitting ||
                                lifecyclePrefillLoading ||
                                selectedIds.length === 0 ||
                                (isLeaveNote && !noteBody.trim()) ||
                                (isLifecycleReport && !lifecycleReportMessage.trim()) ||
                                (isRunDeviceValidation &&
                                    (!selectedValidationEventId || !selectedValidationRaceId)) ||
                                (isRemoveDeviceMapping && !removeMappingConfirmed)
                            }
                            className="inline-flex min-w-[4.5rem] items-center justify-center rounded-md bg-blue-600 px-3 py-1.5 text-[11px] font-semibold text-white shadow-sm hover:bg-blue-700 disabled:opacity-50"
                            aria-busy={submitting}
                            aria-label={submitting ? 'Submit in progress' : undefined}
                        >
                            {submitting ? <InlineSpinner size="xs" className="border-white border-t-transparent" /> : 'Submit'}
                        </button>
                    </div>
                </div>
            </div>
            </div>
        </PortaledLayer>
    );
}
